import { Badge, Button, DatePicker, Divider, Form, Modal, Select, Skeleton, Tooltip } from 'antd';
import API from 'API';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PlusCircleFilled, CheckCircleFilled } from "@ant-design/icons";
import { Calendar, dayjsLocalizer } from 'react-big-calendar';
import dayjs from 'dayjs'
import 'react-big-calendar/lib/css/react-big-calendar.css';
import toastr from 'toastr';

const localizer = dayjsLocalizer(dayjs)

const convertToDate = (baseDate, dayOfWeek, minutes) => {
    const date = new Date(baseDate);
    const diff = dayOfWeek - 1;
    date.setDate(baseDate.getDate() + diff);
    date.setHours(Math.floor(minutes / 60));
    date.setMinutes(minutes % 60);
    date.setSeconds(0);
    return date;
};

const today = new Date();
today.setDate(today.getDate() - ((today.getDay() + 6) % 7));

function TimeTableForm({ timeTable, subjects, setSelectedTimeTable, setChanged, setChosenEvent }) {
    const [form] = Form.useForm();

    useEffect(() => {
        if (timeTable.id === -1) {
            form.resetFields();
            return;
        }
        form.setFieldsValue({
            subject_id: timeTable.subject.id,
            start_time: dayjs().hour(Math.floor(timeTable.start_time / 60)).minute(timeTable.start_time % 60),
            end_time: dayjs().hour(Math.floor(timeTable.end_time / 60)).minute(timeTable.end_time % 60),
            day_of_week: timeTable.day_of_week,
        })
    }, [timeTable, form])

    return <Form
        form={form}
        layout="vertical"
        onFinish={(values) => {
            values.subject = subjects.find(subject => subject.id === values.subject_id);
            values.start_time = values.start_time.hour() * 60 + values.start_time.minute();
            values.end_time = values.end_time.hour() * 60 + values.end_time.minute();
            if (timeTable.id !== -1) {
                timeTable = { ...timeTable, ...values };
                setSelectedTimeTable(
                    selectedTimeTable => {
                        const newTimeTable = { ...selectedTimeTable };
                        newTimeTable.timetables = newTimeTable.timetables.map(item => item.id === timeTable.id ? timeTable : item);
                        return newTimeTable;
                    }
                );
            } else {
                setSelectedTimeTable(
                    selectedTimeTable => {
                        const newTimeTable = { ...selectedTimeTable };
                        newTimeTable.timetables = [...newTimeTable.timetables, { ...values, id: Math.random() * 1000000 }];
                        return newTimeTable;
                    }
                );
            }
            setChanged(true);
            setChosenEvent(null);
            form.resetFields();
        }}
    >
        <Form.Item label="Môn học" name="subject_id" rules={[{ required: true, message: "Vui lòng chọn môn học" }]}>
            <Select
                style={{ width: "100%" }}
                options={subjects.map(subject => ({ label: subject.name, value: subject.id }))}
            />
        </Form.Item>
        <Form.Item
            label="Ngày trong tuần"
            name="day_of_week"
            rules={[{ required: true, message: "Vui lòng chọn ngày trong tuần" }]}
        >
            <Select
                style={{ width: "100%" }}
                options={[
                    { label: "Thứ 2", value: 1 },
                    { label: "Thứ 3", value: 2 },
                    { label: "Thứ 4", value: 3 },
                    { label: "Thứ 5", value: 4 },
                    { label: "Thứ 6", value: 5 },
                ]}
            />
        </Form.Item>
        <Form.Item
            label="Bắt đầu tiết"
            name="start_time"
            rules={[{ required: true, message: "Vui lòng chọn thời gian bắt đầu tiết" }]}
        >
            <DatePicker
                style={{ width: "100%" }}
                picker="time"
                format="HH:mm"
            />
        </Form.Item>
        <Form.Item
            label="Kết thúc tiết"
            name="end_time"
            rules={[
                { required: true, message: "Vui lòng chọn thời gian kết thúc tiết" },
                ({ getFieldValue }) => ({
                    validator(_, value) {
                        const start = getFieldValue('start_time')
                        if (!value || !start || value.isAfter(start)) {
                            return Promise.resolve()
                        }
                        return Promise.reject(
                            new Error('Thời gian kết thúc phải lớn hơn thời gian bắt đầu')
                        )
                    },
                }),
            ]}
        >
            <DatePicker
                style={{ width: "100%" }}
                picker="time"
                format="HH:mm"
            />
        </Form.Item>
        <Button type='primary' htmlType='submit'>
            {timeTable.id === -1 ? "Thêm tiết học" : "Sửa tiết học"}
        </Button>
    </Form>
}

export default function TimeTable() {
    const [classes, setClasses] = useState([]);
    const [gettingClasses, setGettingClasses] = useState(true);
    const [selectedClass, setSelectedClass] = useState(null);
    const [timeTables, setTimeTables] = useState([]);
    const [gettingTimeTables, setGettingTimeTables] = useState(true);
    const [selectedTimeTable, setSelectedTimeTable] = useState(null);
    const [chosenEvent, setChosenEvent] = useState(null);
    const [subjects, setSubjects] = useState([]);
    const [gettingSubjects, setGettingSubjects] = useState(false);
    const [changed, setChanged] = useState(false);
    const [updating, setUpdating] = useState(false);

    useEffect(() => {
        setGettingClasses(true);
        API.get("/classes/")
            .then(res => {
                setClasses(res.data.results);
                if (res.data.results.length > 0) {
                    setSelectedClass(res.data.results[0]);
                }
                setGettingClasses(false);
            })
            .catch(error => {
                console.log(error);
            })
    }, []);
    useEffect(() => {
        if (!selectedClass) return;
        setGettingSubjects(true);
        API.get("/subjects/", { params: { page_size: 100, grade_id: selectedClass.grade } })
            .then(res => {
                setSubjects(res.data.results);
                setGettingSubjects(false);
            })
            .catch(error => {
                console.log(error);
            })
    }, [selectedClass])

    const getTimeTables = useCallback(() => {
        setGettingTimeTables(true);
        API.get("/timetables", { params: { class_id: selectedClass.id } })
            .then(res => {
                setTimeTables(res.data.results);
                if (res.data.results.length > 0) {
                    var timeTable = res.data.results.find(item => item.active);
                    if (!timeTable) timeTable = res.data.results[res.data.results.length - 1];
                    setSelectedTimeTable(timeTable);
                }
                setGettingTimeTables(false);
            })
            .catch(error => {
                console.log(error);
            })
    }, [selectedClass])

    useEffect(() => {
        if (!selectedClass) return;
        getTimeTables();
    }, [selectedClass, getTimeTables]);

    const events = useMemo(() => selectedTimeTable ? selectedTimeTable.timetables.map(item => ({
        id: item.id,
        timeTable: item,
        title: item.subject.name,
        start: convertToDate(today, item.day_of_week, item.start_time),
        end: convertToDate(today, item.day_of_week, item.end_time),
        allDay: false,
    })) : [], [selectedTimeTable]);

    const eventStyleGetter = (event) => {
        const backgroundColor = event.title === 'Ra chơi' ? 'green' : '#0A2A69'
        return { style: { backgroundColor, color: 'white', fontSize: 12 } }
    }

    const updateTimeTable = useCallback(() => {
        setUpdating(true);
        API.post("create-or-update-time-table/", {
            class_id: selectedClass.id,
            timetable_id: selectedTimeTable.id,
            timetables: selectedTimeTable.timetables.map(item => ({ ...item, subject: item.subject.id })),
        })
            .then(res => {
                toastr.success((selectedTimeTable.id === -1 ? "Tạo" : "Cập nhật") + " thời khoá biểu thành công!");
                setChanged(false);
                getTimeTables();
            })
            .catch(error => {
                console.log(error);
            })
            .finally(() => {
                setUpdating(false);
            })
    }, [selectedClass, selectedTimeTable, getTimeTables])

    return gettingClasses || gettingSubjects ? <Skeleton active /> : <>
        <Select
            style={{ width: "100%" }}
            options={classes.map(item => ({ label: `${item.name} - ${item.year}`, value: item.id }))}
            value={selectedClass?.id}
            onChange={value => setSelectedClass(classes.find(item => item.id === value))}
        />
        <Divider />
        {classes.length === 0 && <i>Giáo viên chưa có lớp</i>}
        {selectedClass && <>
            {gettingTimeTables ? <Skeleton active /> : <>
                {timeTables.length === 0 ? <i>Chưa có thời khóa biểu</i> : <div>
                    {timeTables.map((timeTable, index) => <Badge offset={[-5, 5]} count={timeTable.active ? <CheckCircleFilled style={{ color: 'green' }}/> : ""} key={timeTable.id}><div className={`box-div ${timeTable.id === selectedTimeTable?.id ? "selected" : ""}`}
                        onClick={() => setSelectedTimeTable(timeTable)}
                    >
                        Thời khoá biểu {index + 1}
                    </div></Badge>)}
                    <Tooltip title="Tạo thời khoá biểu mới">
                        <div className={`box-div ${selectedTimeTable?.id === -1 ? "selected" : ""}`} onClick={() => {
                            setSelectedTimeTable({
                                id: -1,
                                timetables: [],
                            });
                        }}>
                            <PlusCircleFilled />
                        </div>
                    </Tooltip>
                </div>}
                <Divider />
                {selectedTimeTable && <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                        <div style={{ display: "inline-block" }}>
                            <Button type='primary' onClick={() => setChosenEvent({
                                id: -1, timeTable: { id: -1 }
                            })}>
                                Thêm tiết mới
                            </Button>
                            {!selectedTimeTable.active && <Button type='primary' loading={updating} disabled={updating} onClick={() => {
                                setUpdating(true);
                                API.post("active-time-table/", { timetable_id: selectedTimeTable.id })
                                .then(res => {
                                    setTimeTables(timeTables.map(item => item.id !== selectedTimeTable.id ? {...item, active: false} : {...item, active: true}));
                                    setSelectedTimeTable({...selectedTimeTable, active: true});
                                })
                                .catch(error => {
                                    console.log(error);
                                })
                                .finally(() => {
                                    setUpdating(false);
                                })
                            }} style={{ backgroundColor: "green", marginLeft: 3 }}>
                                Sử dụng thời khoá biểu này
                            </Button>}
                        </div>
                        {(selectedTimeTable.id === -1 || changed) && <Button type='primary' danger onClick={updateTimeTable} loading={updating} disabled={updating}>
                            {selectedTimeTable.id === -1 ? "Tạo thời khoá biểu mới" : "Cập nhật thời khoá biểu"}
                        </Button>}
                    </div>
                    <Calendar
                        localizer={localizer}
                        events={events}
                        defaultView="week"
                        views={['week']}
                        step={15}
                        defaultDate={today}
                        onSelectEvent={(e) => setChosenEvent(e)}
                        eventPropGetter={eventStyleGetter}
                        min={new Date(today.setHours(8, 0))}
                        max={new Date(today.setHours(17, 0))}
                        components={{
                            toolbar: ()=><div/>,
                        }}
                    />
                </>}
            </>}
            <Modal
                title={chosenEvent?.title || "Thêm tiết học"}
                open={chosenEvent !== null}
                onCancel={() => setChosenEvent(null)}
                footer={null}
            >
                {chosenEvent && <TimeTableForm
                    timeTable={chosenEvent.timeTable}
                    subjects={subjects}
                    setSelectedTimeTable={setSelectedTimeTable}
                    setChanged={setChanged}
                    setChosenEvent={setChosenEvent}
                />}
            </Modal>
        </>}
    </>
}